import {ChangeDetectionStrategy, Component} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {AuthService} from "../_services/auth.service";
import {ContentService} from "../_services/content.service";
import {SnackbarService} from "../_services/snackbar.service";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {ConfirmDialogComponent} from "../shared/confirm-dialog/confirm-dialog.component";
import {ConfirmationDialogData} from "../_types/shared";
import {NewPermissionComponent} from "../admin/users/user/new-permission/new-permission.component";
import {ReducedUser, User} from "../_types/user";
import {ReducedTimeline, TimelinePermission} from "../_types/memorybox";
import {ImageCroppedEvent} from "ngx-image-cropper";

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrl: './profile.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProfileComponent {
    user!: User;
    profile: ReducedTimeline | ReducedUser | undefined;
    id: number = 0;
    timelineId: number = 0;
    permissions: TimelinePermission[] | null = null;
    imgURL: string | ArrayBuffer = 'assets/images/blankProfile.png';
    photoFile: string | ArrayBuffer | null = null;
    showCropper: boolean = false;
    isRaa: boolean = false;
    imageChangedEvent: Event | null = null;

    constructor(private snackbarService: SnackbarService,
                private route: ActivatedRoute,
                private authService: AuthService,
                private contentService: ContentService,
                private router: Router,
                public dialog: MatDialog) {
        this.authService.getUser().subscribe({
            next: r => {
                this.user = r;
            }
        })

        this.authService.getDynamicSettings().then((r): void => {
            this.isRaa = r.is_raa;
        });
        this.route.params
            .subscribe({
                next: params => {
                    this.id = params['id'];
                    this.timelineId = params['timelineId'];
                    if (this.id == 0 && this.timelineId) {
                        this.contentService.viewTimeline(this.timelineId).subscribe({
                            next: r => {
                                this.profile = r;
                                if (this.profile.owner) {
                                    this.profile = this.profile.owner;
                                }
                                if (this.profile?.photo) {
                                    this.imgURL = this.profile.photo;
                                }
                                if (!r?.id) {
                                    this.router.navigate(['/people']);
                                    this.snackbarService.openSnackBar('Sorry this timeline does not exist', 'error')
                                    return;
                                }
                                if (!this.canAccessProfile(r.owner?.id)) {
                                    this.snackbarService.openSnackBar('Sorry your user does not have access to view this profile', 'error')
                                    this.router.navigate(['/noAccess']);
                                }
                                if (this.canViewProfileDetails) {
                                    this.getTimelinePermissions();
                                }
                            }, error: error => {
                                this.router.navigate(['/people']);
                                this.snackbarService.openSnackBar(error, 'error')
                                console.error(error)
                            }
                        });
                    } else {
                        if (!this.canAccessProfile(this.id)) {
                            this.snackbarService.openSnackBar('Sorry your user does not have access to view this profile', 'error')
                            this.router.navigate(['/noAccess']);
                        }

                        this.contentService.getUserProfile(this.id).subscribe({
                            next: r => {
                                this.profile = r;
                                if (r.photo) {
                                    this.imgURL = r.photo;
                                }
                            }, error: error => {
                                this.router.navigate(['/people']);
                                this.snackbarService.openSnackBar(error, 'error')
                                console.error(error)
                            }
                        });
                    }
                }, error: error => {
                    this.snackbarService.openSnackBar(error, 'error')
                    console.error(error)
                }
            });
    }

    get isRaaAdoptiveFamilyPictureOnlyMode(): boolean {
        return this.isRaa && this.user?.role.name === 'Adoptive Family';
    }

    get canEditProfilePicture(): boolean {
        return !!this.user?.role.can_edit_profile_pictures || this.isRaaAdoptiveFamilyPictureOnlyMode;
    }

    get canViewProfileDetails(): boolean {
        return !this.isRaaAdoptiveFamilyPictureOnlyMode;
    }

    canAccessProfile(profileId?: number): boolean {
        return this.user.role.can_access_profiles || this.user.id === profileId || this.isRaaAdoptiveFamilyPictureOnlyMode;
    }

    isProfile(): boolean {
        if (this.profile && "owner" in this.profile) {
            // is memory box so check if current user is owner
            return this.user.id === this.profile.owner?.id;
        } else {
            // is user so check if current user is same user
            return this.user.id === this.profile?.id;
        }
    }

    profileDisplayName(): string {
        return this.authService.getDisplayName(this.profile);
    }

    back(): void {
        if (history.length > 1) {
            history.back();
        } else {
            this.router.navigate(['/people']);
        }
    }

    getTimelinePermissions(): void {
        this.contentService.viewTimelinePermissions(this.timelineId).subscribe({
            next: r => {
                this.permissions = r;
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    refreshTimelinePermissions(): void {
        this.permissions = null;
        this.getTimelinePermissions();
    }

    imageCropped(event: ImageCroppedEvent): void {
        if (!event.blob) return;
        let file: File = new File([event.blob], "profile_picture.png");
        this.getBase64(file).then(data => {
            this.photoFile = data;
        });
        const reader: FileReader = new FileReader();
        reader.readAsDataURL(event.blob);
        const that: this = this;
        reader.onloadend = function (): void {
            const base64data: string | ArrayBuffer | null = reader.result;
            if (base64data) {
                that.imgURL = base64data;
            }
        };
    }

    removeImage(): void {
        this.imgURL = this.profile?.photo || 'assets/images/blankProfile.png';
        this.showCropper = false;
    }

    onFileChanged(event: Event): void {
        const element = event.currentTarget as HTMLInputElement;
        const fileList: FileList | null = element.files;
        if (!fileList || fileList.length <= 0) return;

        const file: File = fileList[0];
        var mimeType: string = file.type;
        if (mimeType.match(/image\/*/) == null) {
            // check if image
            this.snackbarService.openSnackBar("Only images are supported.", 'error')
            return;
        }

        var reader: FileReader = new FileReader();
        reader.readAsDataURL(file);
        this.showCropper = true;
        this.imageChangedEvent = event;
        reader.onload = (_event): void => {
            if (reader.result) {
                this.imgURL = reader.result;
            } else {
                this.snackbarService.openSnackBar("Reader result returned null.", 'error')
            }
        }
        this.getBase64(file).then(data => {
            this.photoFile = data;
        });
    }

    getBase64(file: Blob): Promise<string | ArrayBuffer | null> {
        return new Promise(resolve => {
            const reader: FileReader = new FileReader();
            reader.onloadend = (): void => {
                resolve(reader.result);
            };
            return reader.readAsDataURL(file);
        });
    }

    saveProfilePic(): void {
        let message: string = 'Profile pictures can be seen by everyone on the platform'
        if (this.isRaa) {
            message = message + ', including birth family and adoptive family members'
        }
        const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open<ConfirmDialogComponent, ConfirmationDialogData>(ConfirmDialogComponent, {
            panelClass: 'mediumWindow',
            data: {
                showSubmitBtn: true,
                showCancelBtn: true,
                message: message,
                title: 'Are you sure you want to set this profile picture?',
                showHeader: true
            }
        });
        dialogRef.afterClosed().subscribe((resp): void => {
            if (!resp) return;
            const isOwnerlessTimelineProfile: boolean =
                this.id == 0 &&
                !!this.timelineId &&
                !('username' in (this.profile ?? {})) &&
                !('email' in (this.profile ?? {}));

            if (isOwnerlessTimelineProfile) {
                this.contentService.patchTimelinePic(this.timelineId, {photo: this.photoFile}).subscribe({
                    next: r => {
                        this.snackbarService.openSnackBar('Profile updated', 'success');
                        location.reload();
                    },
                    error: error => {
                        this.snackbarService.openSnackBar(error, 'error');
                        console.error(error);
                    }
                });
                return;
            } else if (this.profile?.id) {
                this.contentService.patchUserPic(this.profile.id, {photo: this.photoFile}).subscribe({
                    next: r => {
                        this.snackbarService.openSnackBar('Profile updated', 'success');
                        location.reload();
                    },
                    error: error => {
                        this.snackbarService.openSnackBar(error, 'error');
                        console.error(error);
                    }
                });
            }
        });
    }

    newRelationship(): void {
        const dialogRef: MatDialogRef<NewPermissionComponent> = this.dialog.open(NewPermissionComponent, {
            data: {
                timeline_id: this.timelineId,
                profile: this.profile,
                title: this.authService.getDisplayName(this.profile),
                currentPermissions: this.permissions
            },
        });
        dialogRef.afterClosed().subscribe((resp): void => {
            if (resp) {
                this.refreshTimelinePermissions()
            }
        });
    }
}
